// The registers (all stored in the shared `items` table) and how each one looks.
import type { FieldSpec } from '../ui/RecordDrawer';

export type ItemType = 'rfi' | 'notice' | 'variation' | 'claim' | 'nda';

export type Item = {
  id: string;
  site_id: string;
  type: ItemType;
  subtype: string | null;
  number: string | null;
  ref_no: string | null;
  title: string;
  description: string | null;
  status: string;
  building_ids: string[];
  buildings_raw: string[];
  org_id: string | null;
  org_raw: string | null;
  directed_to: string[];
  directed_raw: string[];
  submitted_on: string | null;
  respond_by: string | null;
  amount: number | null;
  amount_basis: 'VEP' | 'VIP' | null;
  notes: string | null;
  remark: string | null;
  response: string | null;
  data: Record<string, unknown>;
  ss_row_id: number | null;
  ss_created_at: string | null;
  ss_created_by: string | null;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
  deleted_at: string | null;
};

export const STATUS: Record<string, { label: string; tone: 'neutral' | 'orange' | 'dark' | 'good' | 'warn'; closed?: boolean }> = {
  open: { label: 'Open', tone: 'orange' },
  in_process: { label: 'In process', tone: 'orange' },
  hold: { label: 'Hold', tone: 'warn' },
  pending: { label: 'Pending', tone: 'orange' },
  approved: { label: 'Approved', tone: 'good' },
  rejected: { label: 'Rejected', tone: 'dark', closed: true },
  complete: { label: 'Complete', tone: 'good', closed: true },
  closed: { label: 'Closed', tone: 'neutral', closed: true },
  paid: { label: 'Paid', tone: 'good', closed: true },
  signed: { label: 'Signed', tone: 'good', closed: true },
  void: { label: 'Void', tone: 'neutral', closed: true },
};
export const statusLabel = (s: string) => STATUS[s]?.label ?? s;
export const isClosed = (s: string) => Boolean(STATUS[s]?.closed);

export type RegisterDef = {
  module: string; // module key = URL segment
  type: ItemType;
  label: string;
  one: string;
  statuses: string[];
  subtypes?: string[];
  money?: 'VEP' | 'VIP';
  hasRespondBy: boolean;
  hasOrg: boolean;
  /** highlighted extra fields (kept in item.data) shown near the top of the item page */
  keyData?: string[];
};

export const REGISTERS: RegisterDef[] = [
  {
    module: 'rfis',
    type: 'rfi',
    label: 'RFIs & site instructions',
    one: 'RFI / SI',
    statuses: ['open', 'closed', 'void'],
    subtypes: ['RFI', 'Architects SI', 'Engineers SI', 'Engineers Approval', 'Client Approval', "KA's Approval"],
    hasRespondBy: true,
    hasOrg: false,
    keyData: ['Project RFI #', 'Official RFI Doc sent', 'Official SI Doc Sent'],
  },
  {
    module: 'notices',
    type: 'notice',
    label: 'Notices (NOPD/GC/DT/EOT)',
    one: 'notice',
    statuses: ['open', 'approved', 'closed', 'void'],
    subtypes: ['NOPD Notice of Potential Delay', '(GC) General Communication', '(DT) Document Transfer', '(EOT) Extension of Time'],
    hasRespondBy: true,
    hasOrg: false,
    keyData: ['Project NOPD #', 'Document Date', 'Official Doc sent'],
  },
  {
    module: 'variations',
    type: 'variation',
    label: 'Variations',
    one: 'variation',
    statuses: ['open', 'in_process', 'hold', 'approved', 'rejected', 'complete', 'void'],
    subtypes: ['Var', 'Claim', 'Additional Project', 'Maintenance Costs', 'Other'],
    money: 'VEP',
    hasRespondBy: true,
    hasOrg: true,
    keyData: ['Ref/CTC #', 'Ready for Certificate', 'Ready to Proceed', 'Progress of works', 'TIME SENSETIVE'],
  },
  {
    module: 'claims',
    type: 'claim',
    label: 'Claims',
    one: 'claim',
    statuses: ['open', 'in_process', 'hold', 'approved', 'rejected', 'paid', 'void'],
    money: 'VIP',
    hasRespondBy: true,
    hasOrg: true,
    keyData: ['Follow-up Next Claim', 'CLIENT/REF #', 'PROJECT #', 'Claim Date', 'Ready for Certification', 'Docu-Sign Date', 'Finance Comments'],
  },
  {
    module: 'ndas',
    type: 'nda',
    label: 'NDAs',
    one: 'NDA',
    statuses: ['pending', 'signed', 'void'],
    hasRespondBy: false,
    hasOrg: true,
    keyData: ['Person Signed', 'Date Signed', 'Saved in Drive', 'Signed Attachment', 'Needs attention'],
  },
];

export const registerFor = (module: string | undefined) => REGISTERS.find((r) => r.module === module);
export const registerOfType = (t: string) => REGISTERS.find((r) => r.type === t);

/** Fields editable in the item drawer (main columns only; Smartsheet extras stay in "More details"). */
export function itemFields(
  def: RegisterDef,
  buildings: { id: string; name: string }[],
  orgs: { id: string; name: string }[],
): FieldSpec[] {
  const f: FieldSpec[] = [
    { key: 'title', label: def.type === 'nda' ? 'Contractor / individual' : 'Topic', type: 'textarea', required: true },
    { key: 'status', label: 'Status', type: 'select', required: true, options: def.statuses.map((s) => ({ value: s, label: statusLabel(s) })) },
  ];
  if (def.subtypes) f.push({ key: 'subtype', label: 'Type', type: 'select', options: def.subtypes.map((s) => ({ value: s, label: s })) });
  if (def.type !== 'nda') f.push({ key: 'ref_no', label: def.type === 'claim' ? 'Claim #' : def.type === 'variation' ? 'VAR ref #' : 'PBS CTC #' });
  f.push({ key: 'building_ids', label: 'Buildings', type: 'multi', options: buildings.map((b) => ({ value: b.id, label: b.name })) });
  if (def.hasOrg) f.push({ key: 'org_id', label: 'Contractor', type: 'select', options: orgs.map((o) => ({ value: o.id, label: o.name })) });
  if (def.type !== 'nda') f.push({ key: 'directed_to', label: 'Directed to', type: 'multi', options: orgs.map((o) => ({ value: o.id, label: o.name })) });
  if (def.money) f.push({ key: 'amount', label: `Amount (FJD ${def.money})`, type: 'number' });
  if (def.type !== 'nda') f.push({ key: 'submitted_on', label: 'Date submitted', type: 'date' });
  if (def.hasRespondBy) f.push({ key: 'respond_by', label: 'Respond by', type: 'date' });
  f.push({ key: 'notes', label: 'Comments / notes', type: 'textarea', wide: true });
  if (def.type !== 'nda') {
    f.push({ key: 'remark', label: def.type === 'variation' ? 'Hold remarks' : 'Remark / directive', type: 'textarea' });
    if (def.type === 'rfi' || def.type === 'notice') f.push({ key: 'response', label: 'Response', type: 'textarea' });
  }
  return f;
}

export const showValue = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  return String(v);
};
